import type { ContactRole, ContactoTarget } from "@career-ops-amplify/shared";

const ROLE_ALIASES: Record<string, ContactRole> = {
  recruiter: "recruiter",
  "hiring manager": "hiring_manager",
  hiring_manager: "hiring_manager",
  peer: "referrer",
  referrer: "referrer",
  interviewer: "interviewer",
  other: "other",
};

function normalizeRole(raw?: string): ContactRole | undefined {
  if (!raw) return undefined;
  const key = raw.toLowerCase().trim();
  return ROLE_ALIASES[key] ?? (key.includes("recruit") ? "recruiter" : undefined);
}

function extractJsonBlock(text: string): unknown | null {
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence?.[1]) {
    try {
      return JSON.parse(fence[1].trim());
    } catch {
      /* fall through */
    }
  }
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start >= 0 && end > start) {
    try {
      return JSON.parse(text.slice(start, end + 1));
    } catch {
      return null;
    }
  }
  return null;
}

function mapTarget(raw: Record<string, unknown>): ContactoTarget | null {
  const name = String(raw.name ?? raw.contactName ?? "").trim();
  if (!name) return null;

  return {
    name,
    role: normalizeRole(String(raw.role ?? raw.type ?? raw.contactType ?? "")),
    title: raw.title ? String(raw.title) : undefined,
    linkedinUrl: raw.linkedinUrl
      ? String(raw.linkedinUrl)
      : raw.linkedin
        ? String(raw.linkedin)
        : undefined,
    email: raw.email ? String(raw.email) : undefined,
    rationale: raw.rationale
      ? String(raw.rationale)
      : raw.justification
        ? String(raw.justification)
        : undefined,
    selected: true,
  };
}

/** Parse contacto AI output into saveable targets. */
export function parseContactoOutput(output: string): ContactoTarget[] {
  const parsed = extractJsonBlock(output);
  if (parsed && typeof parsed === "object") {
    const obj = parsed as Record<string, unknown>;
    const list = obj.targets ?? obj.contacts ?? obj.alternativeTargets;
    if (Array.isArray(list)) {
      return list
        .map((item) =>
          item && typeof item === "object"
            ? mapTarget(item as Record<string, unknown>)
            : null,
        )
        .filter((t): t is ContactoTarget => t !== null);
    }
  }

  const targets: ContactoTarget[] = [];
  const sectionRe =
    /(?:^|\n)(?:#{1,3}\s*)?(Recruiter|Hiring Manager|Peer|Interviewer)[^\n]*[-–—:]\s*([^\n(]+)/gi;
  let match: RegExpExecArray | null;
  while ((match = sectionRe.exec(output)) !== null) {
    const name = match[2].trim();
    if (name) {
      targets.push({
        name,
        role: normalizeRole(match[1]),
        selected: true,
      });
    }
  }
  return targets;
}
